import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { ROLE_LABELS, STATUS_META, CHAPTERS, isLimitedMemberView } from "@/lib/constants";
import { MemberAvatar } from "@/pages/Members";
import { LiveCertificate } from "@/components/MemberCertificate";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PortalMembersTable } from "@/components/PortalMembersTable";
import { TrendChart } from "@/components/TrendChart";
import {
  Users, CheckCircle, Clock, Hourglass, ArrowRight, PersonSimpleWalk, Warning, Gauge, MapPin, Buildings, IdentificationBadge,
} from "@phosphor-icons/react";

const KPIS = [
  { key: "total", label: "Total Members", icon: Users, color: "text-primary" },
  { key: "approved", label: "Approved", icon: CheckCircle, color: "text-emerald-600 dark:text-emerald-400" },
  { key: "pending_regional", label: "Pending Regional", icon: Clock, color: "text-amber-600 dark:text-amber-400" },
  { key: "pending_national", label: "Pending National", icon: Hourglass, color: "text-blue-600 dark:text-blue-400" },
  { key: "incomplete", label: "Incomplete", icon: Clock, color: "text-slate-500" },
];

export default function Dashboard() {
  const { user } = useAuth();
  const limitedView = isLimitedMemberView(user.role);
  const [activeTab, setActiveTab] = useState("overview");
  const portalDashboards = ["facility_manager", "regional_manager", "national_manager", "super_admin"].includes(user.role);
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [trend, setTrend] = useState(null);
  const [complaintCount, setComplaintCount] = useState(null);

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data));
    api.get("/members", { params: { page_size: 6 } }).then((r) => setRecent(r.data.items));
    if (["regional_manager", "national_manager", "super_admin"].includes(user.role)) {
      api.get("/stats/trend").then((r) => setTrend(r.data)).catch(() => {});
    }
  }, [user.role]);

  useEffect(() => {
    if (!["national_manager", "super_admin"].includes(user.role)) return;
    let active = true;
    const load = () => api.get("/complaints", { params: { view: "all", status: "submitted" } }).then(({ data }) => { if (active) setComplaintCount(data.total); }).catch(() => {});
    load(); const timer = setInterval(() => { if (!document.hidden) load(); }, 15000);
    return () => { active = false; clearInterval(timer); };
  }, [user.role]);

  const isRegionalUp = ["regional_manager", "national_manager", "super_admin"].includes(user.role);
  const isNat = ["national_manager", "super_admin"].includes(user.role);
  let kpiList = KPIS;
  if (isNat) kpiList = [...kpiList,
    { key: "retiring_count", label: "Retiring (60+)", icon: PersonSimpleWalk, color: "text-primary" },
    { key: "avg_completion", label: "Avg Profile Completion", icon: Gauge, color: "text-teal-600 dark:text-teal-400", suffix: "%" },
  ];
  const overdueTotal = stats?.overdue_applications || 0;

  const chapterName = CHAPTERS.find((c) => c.prefix === user.chapter_prefix)?.name;
  const scopeBadges = [];
  if (user.role === "regional_manager" && user.region_name)
    scopeBadges.push({ icon: MapPin, label: `${user.region_name} Region`, testId: "scope-region" });
  if (user.role === "facility_manager") {
    if (user.facility_name) scopeBadges.push({ icon: Buildings, label: user.facility_name, testId: "scope-facility" });
    if (user.region_name) scopeBadges.push({ icon: MapPin, label: `${user.region_name} Region`, testId: "scope-region" });
  }
  if (user.role === "chapter_manager" && (chapterName || user.chapter_prefix))
    scopeBadges.push({ icon: IdentificationBadge, label: chapterName || user.chapter_prefix, testId: "scope-chapter" });

  return (
    <Layout>
      <div className="animate-fade-up">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Overview</span>
            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">
              {ROLE_LABELS[user.role]} Dashboard
            </h1>
            {scopeBadges.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3" data-testid="dashboard-scope-badges">
                {scopeBadges.map((b) => (
                  <span key={b.testId} data-testid={b.testId}
                    className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 text-primary px-3 py-1 text-sm font-medium">
                    <b.icon size={15} weight="bold" /> {b.label}
                  </span>
                ))}
              </div>
            )}
            <p className="text-sm text-muted-foreground mt-2">
              {["national_manager", "super_admin"].includes(user.role)
                ? "You have visibility across all regions, chapters and facilities."
                : "Showing only members within your assigned scope."}
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:items-end">
            {isRegionalUp && <Button variant="outline" onClick={() => navigate("/notifications")} data-testid="dashboard-open-notifications">Notifications</Button>}
            {isNat && <p className="text-sm text-muted-foreground">Export is available in the Members directory.</p>}
          </div>
        </div>

        <LiveCertificate />
        {isNat && complaintCount !== null && <div className="mt-6 p-5 border border-primary/20 bg-primary/5 rounded-md flex flex-wrap justify-between items-center gap-3" data-testid="dashboard-complaints-card"><div><p className="font-display text-lg font-semibold">Member Complaints</p><p className="text-sm text-muted-foreground" data-testid="dashboard-complaints-count">{complaintCount} new complaints awaiting National attention</p></div><Button variant="outline" onClick={() => navigate("/complaints")} data-testid="dashboard-open-complaints">Open Complaints</Button></div>}
        {portalDashboards && <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-6"><TabsList className="max-w-full overflow-x-auto justify-start">
          <TabsTrigger value="overview" data-testid="dashboard-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="intern" data-testid="dashboard-tab-interns">Interns</TabsTrigger>
          {!limitedView && <TabsTrigger value="private" data-testid="dashboard-tab-private">Private Members</TabsTrigger>}
        </TabsList></Tabs>}
        {activeTab === "overview" ? <>
        {/* SLA alert */}
        {isRegionalUp && overdueTotal > 0 && (
          <div data-testid="sla-alert" className="mt-6 flex items-start gap-3 rounded-lg border border-destructive/30 bg-destructive/5 px-5 py-4">
            <Warning size={22} weight="fill" className="text-destructive mt-0.5 shrink-0" />
            <div>
              <p className="font-semibold text-destructive">Attention needed — items past the {stats.sla_days}-day SLA</p>
              <p className="text-sm text-muted-foreground mt-0.5">
                {stats.overdue_applications} application{stats.overdue_applications !== 1 ? "s" : ""} {stats.overdue_applications !== 1 ? "have" : "has"} been waiting too long. Review the Approvals queue.
              </p>
            </div>
          </div>
        )}

        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mt-8">
          {kpiList.map((k, i) => (
            <Card key={k.key} data-testid={`kpi-${k.key}`}
              className={`p-5 border-border border-t-[3px] ${i % 2 ? "border-t-success" : "border-t-primary"} transition-[transform,box-shadow] duration-200 hover:-translate-y-1 hover:shadow-md`}
              style={{ animationDelay: `${i * 40}ms` }}>
              <k.icon size={22} weight="bold" className={k.color} />
              <p className="mt-4 font-display text-3xl font-bold tabular-nums">
                {stats ? (stats[k.key] ?? 0) : "—"}{k.suffix && stats ? k.suffix : ""}
              </p>
              <p className="text-xs text-muted-foreground mt-1">{k.label}</p>
            </Card>
          ))}
        </div>

        {/* Region trends */}
        {isRegionalUp && trend && (
          <Card className="mt-8 border-border p-6" data-testid="trend-card">
            <h2 className="font-display text-lg font-bold">New approvals per month</h2>
            <p className="text-sm text-muted-foreground mt-1">Approved members over the last 12 months, by region.</p>
            <TrendChart months={trend.months} series={trend.series} totals={trend.totals} />
          </Card>
        )}

        {/* Breakdown by unit */}
        {stats?.by_unit?.length > 0 && (
          <Card className="mt-8 border-border p-6" data-testid="dashboard-breakdown">
            <h2 className="font-display text-lg font-bold">{stats.by_unit_label || "Breakdown"}</h2>
            <div className="mt-5 space-y-3">
              {stats.by_unit.map((u) => {
                const max = Math.max(...stats.by_unit.map((x) => x.total), 1);
                return (
                  <div key={u.name} data-testid={`breakdown-${u.name}`}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="font-medium truncate pr-3">{u.name}</span>
                      <span className="text-muted-foreground tabular-nums shrink-0">
                        {u.approved}/{u.total} approved
                      </span>
                    </div>
                    <div className="h-2 rounded-full bg-muted overflow-hidden">
                      <div className="h-full rounded-full bg-primary transition-all duration-500"
                        style={{ width: `${(u.total / max) * 100}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {/* Recent members */}
        <Card className="mt-8 border-border overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-border">
            <h2 className="font-display text-lg font-bold">Recent Members</h2>
            <Button variant="ghost" size="sm" onClick={() => navigate("/members")}
              data-testid="view-all-members" className="gap-1 text-primary">
              View all <ArrowRight size={16} />
            </Button>
          </div>
          <div className="divide-y divide-border">
            {recent.length === 0 && (
              <p className="px-6 py-8 text-sm text-muted-foreground">No members in your scope yet.</p>
            )}
            {recent.map((m) => {
              const st = STATUS_META[m.is_deceased ? "deceased" : m.application_status] || STATUS_META.incomplete;
              return (
                <div key={m.id} data-testid={`recent-member-${m.id}`} className="flex items-center justify-between gap-3 px-6 py-3.5 hover:bg-accent/40 transition-colors">
                  {limitedView && <MemberAvatar member={m} testIdPrefix="recent-member-avatar" />}
                  <div className="min-w-0 flex-1">
                    <p className="font-medium truncate">{m.full_name || [m.first_name, m.middle_name, m.last_name].filter(Boolean).join(" ")}</p>
                    {limitedView ? <>
                      <p className="text-xs text-muted-foreground truncate" data-testid={`recent-member-facility-${m.id}`}>{m.facility_name || "—"}</p>
                      <p className="text-xs text-muted-foreground" data-testid={`recent-member-grade-${m.id}`}>{m.professional_grade || "—"}</p>
                    </> : <p className="text-xs text-muted-foreground font-mono">{m.staff_id}</p>}
                  </div>
                  <div className="flex items-center gap-4">
                    {!limitedView && <span className="font-mono text-sm text-muted-foreground hidden sm:inline">
                      {m.membership_id || "—"}
                    </span>}
                    <span data-testid={`recent-member-status-${m.id}`} className={`text-xs font-medium px-2.5 py-1 rounded-full ${st.cls}`}>{st.label}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
        </> : <PortalMembersTable key={activeTab} memberType={activeTab} />}
      </div>
    </Layout>
  );
}
